import { normalizeDemandPage } from '../../agency/normalizeDemandPage';
import { DashboardTableStatusTone } from '@shigo-ui/dashboard-ui';
import { nullableOptional } from '@shigo-ui/api-client';
import { z } from 'zod';

import { dashboardT } from '../../../lib/messages';

export enum CreativeTab {
  Display = 'display',
  Video = 'video',
}

export enum CreativeEditorMode {
  Create = 'create',
  Edit = 'edit',
}

export enum CreativeFileKind {
  Image = 'image',
  Video = 'video',
}

export enum CreativeType {
  Display = 'DISPLAY',
  Video = 'MEDIAFILE',
}

export enum CreativeSubType {
  Image = 'IMAGE',
  Html = 'HTML',
}

export enum CreativeHostedMode {
  Shigo = 'madSense',
  VastTag = 'vastTag',
  VastUrl = 'vastUrl',
}

export enum CreativeStatus {
  Active = 'ACTIVE',
  Paused = 'PAUSED',
  Archived = 'ARCHIVED',
}

export enum CreativeSortDirection {
  Ascending = 'ASC',
  Descending = 'DESC',
}

export const creativeVariantSchema = z.looseObject({
  id: nullableOptional(z.coerce.string()),
  fileName: nullableOptional(z.string()),
  fileLocation: nullableOptional(z.string()),
  url: nullableOptional(z.string()),
  s3Url: nullableOptional(z.string()),
  bitrate: nullableOptional(z.number()),
  width: nullableOptional(z.number()),
  height: nullableOptional(z.number()),
  active: nullableOptional(z.boolean()),
});

const creativeContractSchema = z
  .looseObject({
    id: z.coerce.string(),
    name: z.coerce.string(),
    hosted: nullableOptional(z.string()),
    type: nullableOptional(z.string()),
    subType: nullableOptional(z.string()),
    content: nullableOptional(z.string()),
    fileName: nullableOptional(z.string()),
    bannerSize: nullableOptional(z.string()),
    url: nullableOptional(z.string()),
    thumbnailUrl: nullableOptional(z.string()),
    width: nullableOptional(z.number()),
    height: nullableOptional(z.number()),
    duration: nullableOptional(z.number()),
    bitrate: nullableOptional(z.number()),
    activeStatus: nullableOptional(z.string()),
    status: nullableOptional(z.string()),
    systemStatus: nullableOptional(z.string()),
    variants: z.array(creativeVariantSchema).optional().default([]),
    lineItemsCnt: nullableOptional(z.number()),
    impressionTrackers: z.array(z.string()).optional().default([]),
    trackerStart: nullableOptional(z.string()),
    trackerFirstQuartile: nullableOptional(z.string()),
    trackerMidpoint: nullableOptional(z.string()),
    trackerThirdQuartile: nullableOptional(z.string()),
    trackerComplete: nullableOptional(z.string()),
    clickThroughUrl: nullableOptional(z.string()),
    createdOn: nullableOptional(z.string()),
    vastTag: nullableOptional(z.string()),
    vastUrl: nullableOptional(z.string()),
  })
  .superRefine((creative, context) => {
    rejectUnsupportedEnum(context, 'type', creative.type, Object.values(CreativeType));
    rejectUnsupportedEnum(context, 'subType', creative.subType, Object.values(CreativeSubType));
    rejectUnsupportedEnum(context, 'hosted', creative.hosted, Object.values(CreativeHostedMode));
    rejectUnsupportedEnum(
      context,
      'activeStatus',
      creative.activeStatus,
      Object.values(CreativeStatus),
    );
  });

export const creativeSchema = creativeContractSchema.transform(
  ({ hosted, type, subType, activeStatus, status, ...creative }) => {
    const normalizedHosted = parseCreativeHostedMode(hosted);
    const normalizedType = parseCreativeType(type);
    const normalizedSubType = parseCreativeSubType(subType);
    const normalizedActiveStatus = parseCreativeStatus(activeStatus ?? status);
    return {
      ...creative,
      ...(normalizedHosted ? { hosted: normalizedHosted } : {}),
      ...(normalizedType ? { type: normalizedType } : {}),
      ...(normalizedSubType ? { subType: normalizedSubType } : {}),
      ...(normalizedActiveStatus ? { activeStatus: normalizedActiveStatus } : {}),
      ...(status !== undefined ? { status } : {}),
    };
  },
);

export const creativesPageSchema = z.preprocess(
  normalizeDemandPage,
  z.looseObject({
    content: z.array(creativeSchema),
    totalElements: z.number(),
    totalPages: z.number().optional(),
    number: z.number().optional(),
    size: z.number().optional(),
  }),
);

export type CreativeVariant = z.infer<typeof creativeVariantSchema>;
export type CreativeDto = z.input<typeof creativeContractSchema>;
export type Creative = z.infer<typeof creativeSchema>;
export type CreativesPage = z.infer<typeof creativesPageSchema>;

export type CreativesFilter = {
  agencyIds?: string[];
  brandId: string;
  name: string;
  type: CreativeType;
  activeStatus: CreativeStatus[];
  page: number;
  size: number;
  sortProperty: readonly string[];
  sortDirection: CreativeSortDirection;
};

export type CreativeMutationPayload = {
  name: string;
  type: CreativeType;
  activeStatus: CreativeStatus;
  subType?: CreativeSubType | undefined;
  bannerSize?: string | undefined;
  content?: string | undefined;
  clickThroughUrl?: string | undefined;
  width?: number | null | undefined;
  height?: number | null | undefined;
  hosted?: CreativeHostedMode | undefined;
  vastTag?: string | undefined;
  vastUrl?: string | undefined;
  creative?: string | undefined;
  impressionTrackers: string[];
  trackerStart?: string | undefined;
  trackerFirstQuartile?: string | undefined;
  trackerMidpoint?: string | undefined;
  trackerThirdQuartile?: string | undefined;
  trackerComplete?: string | undefined;
  variants?: CreativeVariant[] | undefined;
};

export const creativePageSizes = [5, 10, 25, 50, 100] as const;
export const creativeStatusOptions = [
  CreativeStatus.Active,
  CreativeStatus.Paused,
  CreativeStatus.Archived,
] as const;

export enum CreativeBannerSize {
  HalfPage = '300x600 Half Page Ad',
  WideSkyscraper = '160x600 Wide Skyscraper',
  Leaderboard = '728x90 Leaderboard',
  LargeLeaderboard = '970x90 Large Leaderboard',
  LargeRectangle = '336x280 Large Rectangle',
  Square = '250x250 Square',
  MediumRectangle = '300x250 Medium Rectangle',
  MobileLeaderboard = '320x50 Mobile Leaderboard',
  MobileBanner = '300x50 Mobile Banner',
  MobileInterstitial = '320x480 Mobile Interstitial',
  MobileLandscapeInterstitial = '480x320 Mobile Landscape Interstitial',
  Banner = '468x60 Banner',
  Custom = '970x250 Custom',
}

export const creativeBannerSizes = [
  { key: CreativeBannerSize.HalfPage, width: 300, height: 600 },
  { key: CreativeBannerSize.WideSkyscraper, width: 160, height: 600 },
  { key: CreativeBannerSize.Leaderboard, width: 728, height: 90 },
  { key: CreativeBannerSize.LargeLeaderboard, width: 970, height: 90 },
  { key: CreativeBannerSize.LargeRectangle, width: 336, height: 280 },
  { key: CreativeBannerSize.Square, width: 250, height: 250 },
  { key: CreativeBannerSize.MediumRectangle, width: 300, height: 250 },
  { key: CreativeBannerSize.MobileLeaderboard, width: 320, height: 50 },
  { key: CreativeBannerSize.MobileBanner, width: 300, height: 50 },
  { key: CreativeBannerSize.MobileInterstitial, width: 320, height: 480 },
  { key: CreativeBannerSize.MobileLandscapeInterstitial, width: 480, height: 320 },
  { key: CreativeBannerSize.Banner, width: 468, height: 60 },
  { key: CreativeBannerSize.Custom, width: 970, height: 250 },
] as const;

/** The listed banner size for a stored or form-provided key, if any. */
export function findCreativeBannerSize(bannerSize: string) {
  return creativeBannerSizes.find((option) => (option.key as string) === bannerSize);
}

/** The listed banner size for stored dimensions, or "WxH" when no listed size matches. */
export function creativeBannerSizeKey(
  width: number | null | undefined,
  height: number | null | undefined,
) {
  if (!width || !height) return '';
  const size = creativeBannerSizes.find(
    (option) => option.width === width && option.height === height,
  );
  return size ? size.key : `${String(width)}x${String(height)}`;
}

export function creativeTypeForTab(tab: CreativeTab) {
  return tab === CreativeTab.Video ? CreativeType.Video : CreativeType.Display;
}

export function defaultCreativesFilter(tab: CreativeTab): CreativesFilter {
  return {
    brandId: '',
    name: '',
    type: creativeTypeForTab(tab),
    activeStatus:
      tab === CreativeTab.Video
        ? [CreativeStatus.Active, CreativeStatus.Paused]
        : [CreativeStatus.Active],
    page: 0,
    size: 25,
    sortProperty: ['createdOn'],
    sortDirection: CreativeSortDirection.Descending,
  };
}

export function serializeCreativesFilter(filter: CreativesFilter) {
  const params = new URLSearchParams({
    page: String(filter.page),
    size: String(filter.size),
    type: filter.type,
    sortProperty: filter.sortProperty.join(','),
    sortDirection: filter.sortDirection,
    activeStatus: filter.activeStatus.join(','),
  });
  if (filter.name.trim()) params.set('name', filter.name.trim());
  return params;
}

export function resolveCreativeStatusLabel(status: CreativeStatus | undefined) {
  if (status === CreativeStatus.Active) return dashboardT('creatives.status.active');
  if (status === CreativeStatus.Paused) return dashboardT('creatives.status.paused');
  if (status === CreativeStatus.Archived) return dashboardT('creatives.status.archived');
  return dashboardT('creatives.status.unknown');
}

export function resolveCreativeStatusTone(status: CreativeStatus | undefined) {
  if (status === CreativeStatus.Active) return DashboardTableStatusTone.Success;
  if (status === CreativeStatus.Paused) return DashboardTableStatusTone.Warning;
  return DashboardTableStatusTone.Neutral;
}

export function formatCreativeDuration(seconds: number | undefined) {
  if (seconds === undefined || seconds < 0) return '-';
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
}

export function serializeCreativeVariantsUpdate(
  creative: Creative,
  variants: CreativeVariant[],
): CreativeMutationPayload {
  const payload: CreativeMutationPayload = {
    name: creative.name,
    type: creative.type ?? CreativeType.Video,
    activeStatus: creative.activeStatus ?? CreativeStatus.Active,
    impressionTrackers: [...creative.impressionTrackers],
    variants: variants.map((variant) => ({ ...variant })),
  };
  if (creative.type === CreativeType.Display) {
    payload.subType = normalizeCreativeSubType(creative.subType);
    payload.width = creative.width ?? null;
    payload.height = creative.height ?? null;
    payload.bannerSize = creative.bannerSize ?? '';
    payload.content = creative.content ?? '';
    payload.clickThroughUrl = creative.clickThroughUrl ?? '';
    return payload;
  }
  payload.hosted = normalizeCreativeHostedMode(creative.hosted);
  copyOptionalTrackers(payload, creative);
  payload.clickThroughUrl = creative.clickThroughUrl ?? '';
  payload.vastTag = creative.vastTag ?? '';
  payload.vastUrl = creative.vastUrl ?? '';
  return payload;
}

export function normalizeCreativeSubType(value: string | undefined) {
  return parseCreativeSubType(value) ?? CreativeSubType.Image;
}

export function normalizeCreativeHostedMode(value: string | undefined) {
  return parseCreativeHostedMode(value) ?? CreativeHostedMode.Shigo;
}

function parseCreativeType(value: string | undefined) {
  return enumValue(CreativeType, value);
}

function parseCreativeSubType(value: string | undefined) {
  return enumValue(CreativeSubType, value);
}

function parseCreativeHostedMode(value: string | undefined) {
  return enumValue(CreativeHostedMode, value);
}

function parseCreativeStatus(value: string | undefined) {
  return enumValue(CreativeStatus, value);
}

function enumValue<TValue extends string>(
  values: Record<string, TValue>,
  value: string | undefined,
): TValue | undefined {
  return value && Object.values(values).includes(value as TValue) ? (value as TValue) : undefined;
}

function rejectUnsupportedEnum(
  context: z.RefinementCtx,
  path: 'type' | 'subType' | 'hosted' | 'activeStatus',
  value: string | undefined,
  supportedValues: readonly string[],
) {
  if (value !== undefined && !supportedValues.includes(value)) {
    context.addIssue({ code: 'custom', path: [path], message: `Unsupported ${path}` });
  }
}

function copyOptionalTrackers(payload: CreativeMutationPayload, creative: Creative) {
  const keys = [
    'trackerStart',
    'trackerFirstQuartile',
    'trackerMidpoint',
    'trackerThirdQuartile',
    'trackerComplete',
  ] as const;
  keys.forEach((key) => {
    payload[key] = creative[key] ?? '';
  });
}
